import extractFromCookies from '../helpers/utils/extractFromCookies';
import {
  ConfirmPage,
  IBookingService,
  IPayloadPostOrderParts,
  IPayloadPostVehicle,
} from '../interface/booking-service.interface';
import api from './api';
import { APIProxy } from './api-proxy';

const API_GATEWAY_URL_SERVICE_BOOKING = process.env.API_GATEWAY_URL_SERVICE_BOOKING;
const API_GATEWAY_URL_CONTENT = process.env.API_GATEWAY_URL_CONTENT;

// BOOKING SERVICES

const getCart = (cartId: string, accessToken?: string) =>
  api.get(`/api/create-update-service`, {
    params: { cartId, accessToken: accessToken || undefined },
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

const postCart = ({ payload, isUpdate }: { payload: any; isUpdate: boolean }) => {
  return api.post(
    `/api/create-update-service?isUpdate=${isUpdate}`,
    null,
    { ...payload },
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );
};

const postSubmitCart = ({ payload }: { payload: ConfirmPage }) => {
  return api.post(
    '/api/submit-cart',
    null,
    { ...payload },
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );
};

const getWorkShopServiceByCarModel = (carModel: string) =>
  api.get(
    `service-booking/v1/layanan-bengkel/workshop-service?carModel=${carModel}`,
    false,
    false,
    false,
    false
  );

const getWorkShopServiceTimeSlot = (payload: any) =>
  api.get(
    `service-booking/v1/layanan-bengkel/get-workshop-service-slots`,
    { params: payload },
    false,
    false,
    false
  );

const getCheckBookingByPoliceNumber = (policeRegNo: string, isLoggedIn = false) => {
  if (isLoggedIn) {
    return api.get(
      `/api/check-booking`,
      {
        baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
        params: {
          policeRegNo,
        },
      },
      false,
      false,
      false
    );
  } else {
    return api.get(
      `service-booking/v1/layanan-bengkel/check-booking`,
      {
        params: {
          policeRegNo,
        },
      },
      false,
      false,
      false
    );
  }
};

// THS

const postVehicle = async ({
  payload,
  isLoggedIn,
}: {
  payload: IPayloadPostVehicle;
  isLoggedIn: boolean;
}) => {
  let response = null;
  if (!isLoggedIn) {
    response = await api.post('customer/v1/vehicle', null, payload);
  } else {
    response = await api.post('/api/post-vehicle', null, payload, {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    });
  }

  return response;
};

const postOrderParts = async (payload: IPayloadPostOrderParts) => {
  const response = await api.post('/api/post-order-parts', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response;
};

const getCheckLastService = async (vin: string) => {
  const response = await api.get(`/api/check-last-service?vin=${vin}`, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response.data;
};

const deleteCustomerVehicle = async (payload: { licencePlate: string; vin?: string | null }) => {
  const response = await api.post('/api/delete-customer-vehicle', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
  return response;
};

const checkTCare = async (payload: { vin: string; policeRegNo?: string }) => {
  const response = await api.post('customer/v1/saphybris/tcare', null, payload);
  return response;
};

const getMechanicLocation = (payload: any) =>
  api.get(
    `service-booking/v1/home-service/mechanic-location`,
    { params: payload },
    false,
    false,
    false
  );

const getHomeServiceByCarModel = (carModel: string) =>
  api.get(
    `service-booking/v1/home-service/home-services?carModel=${carModel}`,
    false,
    false,
    false,
    false
  );

const getVehicleMaster = (payload: any) =>
  api.get(`service-booking/v1/vehicles/vehicle-master`, { params: payload }, false, false, false);

const getServiceDataByOrderNumber = (
  payload: { orderNumber: string },
  req,
  res,
  isUseGatewayAPI?: boolean
) => {
  const apiProxy = new APIProxy(req, res);
  const accessToken = extractFromCookies('access-token', req?.headers?.cookie);
  return apiProxy.makeRequest({
    method: req.method,
    url: 'service-booking/v1/service-data/get-service-data-by-order-number',
    ...(accessToken && { headers: { Authorization: 'Bearer ' + accessToken } }),
    ...(isUseGatewayAPI
      ? {
          baseURL: API_GATEWAY_URL_SERVICE_BOOKING,
          params: { orderNumber: payload.orderNumber, notificationType: '-' },
        }
      : { params: { orderNumber: payload.orderNumber, notificationType: '-' } }),
  });
};

const getServiceBooking = (payload: any) =>
  api.get(`service-booking/v1/service-bookings`, { params: payload }, false, false, false);

const getTestDriveBranch = (payload: any) =>
  api.get(`service-booking/v1/branch`, { params: payload }, false, false, false);

const getBranchHoliday = (payload: any) =>
  api.get(`service-booking/v1/branch/holiday`, { params: payload }, false, false, false);

// Body & Paint

const getBodyPaintServiceSlots = () =>
  api.get(`service-booking/v1/body-paint-service/slots`, false, false, false, false);

const getBodyPaint = () =>
  api.get(`service-booking/v1/body-paint-service/get-body-paint`, false, false, false, false);

const createBookingRequest = (payload) => {
  return api.post('customer/v1/booking-simple', null, payload);
};

const getListBookingService = ({
  code,
  isUseGatewayAPI = true,
  branchType,
}: {
  code?: string;
  isUseGatewayAPI?: boolean;
  branchType?: string;
}): Promise<{ data: { content: IBookingService[] } }> => {
  return api.get('content/v1/contentCatalog/after-sales-product', {
    params: { code, branchType },
    ...( isUseGatewayAPI && { baseURL: API_GATEWAY_URL_CONTENT }),
  });
};

const checkAutoConvert = (payload: { policeRegNo: string; mobilePhone: string }) =>
  api.post('service-booking/v1/layanan-bengkel/check-auto-convert', null, payload, false, false);

const getHomeServiceSlots = (payload: {
  slotType: string;
  serviceLocation: {
    longitude: number;
    latitude: number;
  };
  totalDuration: number;
  scheduleStartDate: string;
  scheduleEndDate: string;
  mechanicNo: string | null;
  policeRegNo: string;
  pageNo: number | null;
  recordsPerpage: number | null;
}) => api.post('service-booking/v1/home-service/home-service-slots', null, payload, false, false);

const getInfoServiceBerkalaDisclaimer = () => api.get('content/v1/page/workshop-disclaimer');

const getSlotsByBranchAndRange = (payload: {
  totalDuration: number;
  scheduleStartDate: string;
  scheduleEndDate: string;
  listOfBranch: string[];
}) =>
  api.post(
    'service-booking/v1/layanan-bengkel/slots-by-branch-and-range',
    null,
    payload,
    false,
    false
  );

export default {
  getCart,
  getWorkShopServiceByCarModel,
  getWorkShopServiceTimeSlot,
  getCheckBookingByPoliceNumber,
  getMechanicLocation,
  getHomeServiceByCarModel,
  getVehicleMaster,
  getServiceDataByOrderNumber,
  getServiceBooking,
  getTestDriveBranch,
  getBranchHoliday,
  getBodyPaintServiceSlots,
  getBodyPaint,
  createBookingRequest,
  postOrderParts,
  getListBookingService,
  postSubmitCart,
  postVehicle,
  postCart,
  checkAutoConvert,
  checkTCare,
  getCheckLastService,
  deleteCustomerVehicle,
  getHomeServiceSlots,
  getInfoServiceBerkalaDisclaimer,
  getSlotsByBranchAndRange,
};
